import { Component, Fragment } from 'react';
import cx from 'classnames';
import { Router } from '../../configs/routes';
import { stringify, parse } from 'query-string';
import styles from '../../styles/plan/investmentCard.scss';
import { roundOffTo500 } from '../../utils/commonUtils';
import {
  calculateSipFutureValue,
  formatRupeeLiteral,
  calculateLumpsumFutureValue,
  MAX_MF_INVEST_IN_LUMPSUM,
  MAX_MF_INVEST_IN_SIP,
} from '../../constants';

const MAP_METHOD_TO_TYPE = {
  monthly: 'SIP_MF',
  oneTime: 'LUMPSUM_MF',
};

interface Props {
  minAmount: number;
  title: string;
  bucketData: any;
  isVariant?: boolean;
  investClick(): void;
  onAmountChangeBlur(e: any): void;
  handleInvestmentTypeChange(amount: number, type: string): void;
}

interface State {
  yearSelected: string;
  payFrequency: string;
  amount: number;
  minAmount: number;
  maxAmount: number;
}

class InvestmentCard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      yearSelected: '3',
      payFrequency: 'monthly',
      amount: 2000,
      minAmount: this.props.minAmount,
      maxAmount: 20000,
    };
  }

  public setCardState = (changeType: boolean, payFrequency?: string) => {
    const { bucketData, handleInvestmentTypeChange, minAmount } = this.props;
    const maxAmount =
      payFrequency === 'monthly'
        ? MAX_MF_INVEST_IN_SIP
        : MAX_MF_INVEST_IN_LUMPSUM;
    const suggestedSipAmount = bucketData.suggestedSipAmount;
    const suggestedLumpSumAmount = bucketData.suggestedLumpSumAmount;
    const amount =
      (payFrequency === 'monthly'
        ? suggestedSipAmount
        : suggestedLumpSumAmount) || minAmount;
    this.setState(
      {
        payFrequency,
        minAmount,
        maxAmount,
        amount,
      },
      () => {
        if (changeType) {
          handleInvestmentTypeChange(
            this.state.amount,
            MAP_METHOD_TO_TYPE[payFrequency]
          );
        }
      }
    );
  };

  public UNSAFE_componentWillMount() {
    this.setCardState(false, 'monthly'); // select sip by default
  }

  public handleYearSelection = (e: any) => {
    if (e.target.getAttribute('data-yr')) {
      this.setState({
        yearSelected: e.target.getAttribute('data-yr'),
      });
    }
  };

  public handlePayFrequency = (e: any) => {
    const payFrequency = e.target.getAttribute('data-id');
    if (payFrequency) {
      const { handleInvestmentTypeChange } = this.props;
      const { amount } = this.state;
      this.setCardState(true, payFrequency);
      handleInvestmentTypeChange(amount, MAP_METHOD_TO_TYPE[payFrequency]);
    }
  };

  public handleSlider = (e: any) => {
    const value = parseInt(e.target.value.replace(/\D/g, ''), 10);
    this.setState({
      amount: Number(value),
    });
  };

  public handleAmountInput = (e: any) => {
    const parsedValue = parseInt(e.target.value.replace(/\D/g, ''), 10);
    this.setState({
      amount: parsedValue || 0,
    });
  };

  private handleInputBlur = (e: any) => {
    const { payFrequency } = this.state;
    const { handleInvestmentTypeChange } = this.props;
    const parsedValue = parseInt(e.target.value.replace(/\D/g, ''), 10);

    // Added to change state(investmentType) of parent component i.e. plan page
    this.setCardState(true, payFrequency);
    handleInvestmentTypeChange(parsedValue, MAP_METHOD_TO_TYPE[payFrequency]);

    this.setState({
      amount: roundOffTo500(parsedValue) || 0,
    });
  };

  private handleChipClick = (e: any) => {
    const { amount } = this.state;
    const dataId = e.target.getAttribute('data-id');
    if (dataId) {
      this.setState({
        amount: dataId === '5000' ? amount + 5000 : amount + 10000,
      });
      const queries = parse(location.search);
      queries.amount = dataId === '5000' ? amount + 5000 : amount + 10000;
      Router.pushRoute(`${location.pathname}?${stringify(queries)}`, {
        shallow: true,
      });
    }
  };

  private renderChips = () => {
    return (
      <Fragment>
        <div className={styles['chip-container']}>
          <div
            data-id="5000"
            className={styles['chip']}
            onClick={this.handleChipClick}
          >
            + &#8377;5,000
          </div>
          <div
            data-id="10000"
            className={styles['chip']}
            onClick={this.handleChipClick}
          >
            + &#8377;10,000
          </div>
        </div>
      </Fragment>
    );
  };

  private renderCalculator = () => {
    const { onAmountChangeBlur, bucketData } = this.props;
    const { yearSelected, payFrequency, amount, maxAmount } = this.state;
    const { minAmount } = this.props;
    const returnPercentage =
      yearSelected === '3'
        ? bucketData.sections.planSummary.returns.return3yr
        : bucketData.sections.planSummary.returns.return5yr;
    const sliderRangeCalculator = Math.floor(
      (Number(amount - minAmount) / (maxAmount - minAmount)) * 100
    );
    let sliderRange;
    if (sliderRangeCalculator < 0) {
      sliderRange = 0;
    } else if (sliderRangeCalculator > 100) {
      sliderRange = 100;
    } else {
      sliderRange = sliderRangeCalculator;
    }
    return (
      <Fragment>
        <div className={cx(styles['slider-container'], 'mb30 relative')}>
          <div
            className={cx(styles['track'])}
            style={{ width: `${sliderRange}%` }}
          />
          <input
            id="slider"
            className={cx(styles['slider'])}
            onChange={this.handleSlider}
            onMouseUp={e => {
              this.handleInputBlur(e);
              onAmountChangeBlur(e);
            }}
            type="range"
            min={minAmount}
            max={maxAmount}
            value={String(amount < maxAmount ? amount : maxAmount)}
            step="500"
          />
        </div>
        <div className={cx(styles['year-selector-container'], 'mb20')}>
          <div>No. of years</div>
          <div
            className={cx(styles['years-container'], 'flex')}
            onClick={this.handleYearSelection}
          >
            <div
              className={cx(yearSelected === '3' ? styles['selected'] : '')}
              data-yr="3"
            >
              3
            </div>
            <div
              className={cx(yearSelected === '5' ? styles['selected'] : '')}
              data-yr="5"
            >
              5
            </div>
            <div
              className={cx(yearSelected === '10' ? styles['selected'] : '')}
              data-yr="10"
            >
              10
            </div>
            {/* <div
                className={cx(yearSelected === '15' ? styles['selected'] : '')}
                data-yr="15"
              >
                15
              </div> */}
          </div>
        </div>
        <div className={cx(styles['est-val-container'], 'mb30')}>
          <div>
            <div>Estimated Value</div>
            <div className="text--sm">(as per historical returns)</div>
          </div>
          <div className="bold font--blue-solid text--xlg">
            ₹
            {payFrequency === 'monthly'
              ? formatRupeeLiteral(
                  calculateSipFutureValue(
                    amount,
                    Number(yearSelected),
                    returnPercentage
                  )
                )
              : formatRupeeLiteral(
                  calculateLumpsumFutureValue(
                    amount,
                    Number(yearSelected),
                    returnPercentage
                  )
                )}
          </div>
        </div>
      </Fragment>
    );
  };

  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const nextMinAmount = nextProps.minAmount;
    const { minAmount } = this.props;

    if (nextMinAmount !== minAmount) {
      this.setState({
        minAmount: nextMinAmount,
      });
    }
  }

  public render() {
    const { title, investClick, onAmountChangeBlur, isVariant } = this.props;
    const { payFrequency, amount } = this.state;
    const { minAmount } = this.props;
    const maxAmount =
      payFrequency === 'monthly'
        ? MAX_MF_INVEST_IN_SIP
        : MAX_MF_INVEST_IN_LUMPSUM;
    return (
      <div id="investment-card" className={cx(styles['card'], 'p15')}>
        <div className={cx(styles['container'])}>
          <div className="center mb20 mt10 text--large bold">{title}</div>
          <div
            className={cx(styles['pay-frequency-container'], 'mb30')}
            onClick={this.handlePayFrequency}
          >
            <div
              className={cx(
                payFrequency === 'monthly' ? styles['selected'] : ''
              )}
              data-id="monthly"
            >
              Monthly
            </div>
            <div
              className={cx(
                payFrequency === 'oneTime' ? styles['selected'] : ''
              )}
              data-id="oneTime"
            >
              One-Time
            </div>
          </div>
          <div
            className={cx(
              styles['amount-container'],
              isVariant ? 'mb15' : 'mb30'
            )}
          >
            <div className="mb5">
              <span className={cx(styles['span'], 'font--faded')}>₹</span>
              <input
                type="text"
                value={amount.toLocaleString('en-IN')}
                onChange={this.handleAmountInput}
                maxLength={10}
                onBlur={e => {
                  this.handleInputBlur(e);
                  onAmountChangeBlur(e);
                }}
              />
            </div>
            {amount < minAmount ? (
              <span className="font--red text--normal">
                Min amount should be ₹ {minAmount.toLocaleString('en-IN')}
              </span>
            ) : null}
            {amount > maxAmount && (
              <span className="font--red text--normal">
                Max amount should be ₹ {maxAmount.toLocaleString('en-IN')}
              </span>
            )}
          </div>

          {isVariant ? this.renderChips() : this.renderCalculator()}

          <div className="mb10">
            <button
              disabled={amount < minAmount || amount > maxAmount}
              className="btn btn-full btn-green-solid hover"
              onClick={amount >= minAmount ? investClick : null}
            >
              INVEST ₹{amount.toLocaleString('en-IN')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default InvestmentCard;



// WEBPACK FOOTER //
// ./components/plan/investmentCard.tsx